import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './AdminPanel.css';

const AdminPanel = () => {
  // === Authentication State & Handlers ===
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);

  // Handler for file input change
const handlePhotoChange = (e) => {
  setPhotoFiles([...e.target.files]);
};
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/admin_login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername, password: loginPassword })
      });
      const data = await res.json();
      if (data.success) {
        setIsAuthenticated(true);
        setLoginError('');
      } else {
        setLoginError(data.message || 'Invalid credentials');
      }
    } catch (err) {
      setLoginError('Login error: ' + err.message);
    }
  };

  // === Admin Panel States & Handlers ===
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    name: '',
    subtitle: '',
    whoAmI: '',
    myBasics: '',
    aboutMyFamily: '',
    myEducation: '',
    residentialInformation: '',
    businessDetails: ''
  });
  const [message, setMessage] = useState('');
  const [shareableLink, setShareableLink] = useState('');
  const [profiles, setProfiles] = useState([]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuillChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };
  const uploadPhotos = async (profileId) => {
    if (photoFiles.length === 0) return [];
    const formData = new FormData();
    formData.append('profileId', profileId);
    photoFiles.forEach((file) => {
      formData.append('photos[]', file);
    });
    try {
      const res = await fetch('/upload_photo.php', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      return data.urls; // Expected to be an array of URLs
    } catch (err) {
      console.error("Photo upload error:", err);
      return [];
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // First, create the profile (text fields only)
      const response = await fetch('/create_profile.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const text = await response.text();
      const data = JSON.parse(text);
      setMessage(data.message);
      setShareableLink(data.shareableLink);
      
      // Assume your create_profile.php returns a profileId in data.profileId
      if (data.profileId && photoFiles.length > 0) {
        const uploadedPhotoUrls = await uploadPhotos(data.profileId);
        if (uploadedPhotoUrls.length > 0) {
          // Update the profile with the photo URLs
          await fetch('/update_profile.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: data.profileId,
              name: formData.name,
              subtitle: formData.subtitle,
              whoAmI: formData.whoAmI,
              myBasics: formData.myBasics,
              aboutMyFamily: formData.aboutMyFamily,
              myEducation: formData.myEducation,
              residentialInformation: formData.residentialInformation,
              businessDetails: formData.businessDetails,
              photos: JSON.stringify(uploadedPhotoUrls)
            })
          });
          
        }
      }
      if (activeTab === 'manage') {
        fetchProfiles();
      }
    } catch (err) {
      setMessage('Error creating profile: ' + err.message);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await fetch('/list_profiles.php');
      const data = await res.json();
      setProfiles(data);
    } catch (err) {
      console.error("Error fetching profiles:", err);
    }
  };

  useEffect(() => {
    if (activeTab === 'manage') {
      fetchProfiles();
    }
  }, [activeTab]);

  const handleRegenerate = async (profileId) => {
    try {
      const res = await fetch('/regenerate_link.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: profileId })
      });
      const data = await res.json();
      if (!data.error) {
        setMessage('Link regenerated successfully');
        fetchProfiles();
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (err) {
      setMessage('Error regenerating link: ' + err.message);
    }
  };

  const handleExpire = async (profileId) => {
    try {
      const res = await fetch('/expire_link.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: profileId })
      });
      const data = await res.json();
      if (!data.error) {
        setMessage('Profile expired successfully');
        fetchProfiles();
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (err) {
      setMessage('Error expiring profile: ' + err.message);
    }
  };
  

  // New helper functions for copy, edit, and delete
  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => alert('Link copied!'))
      .catch(() => alert('Failed to copy link'));
  };

  // Inside AdminPanel component
const [editingProfile, setEditingProfile] = useState(null);
const [editFormData, setEditFormData] = useState({
  name: '',
  subtitle: '',
  whoAmI: '',
  myBasics: '',
  aboutMyFamily: '',
  myEducation: '',
  residentialInformation: '',
  businessDetails: ''
});

const handleEdit = async (profile) => {
  // If your profile from list is incomplete, fetch full details
  const res = await fetch(`/get_profile_by_id.php?id=${profile.id}`);
  const fullProfile = await res.json();
  setEditingProfile(fullProfile);
  setEditFormData({
    name: fullProfile.name,
    subtitle: fullProfile.subtitle,
    whoAmI: fullProfile.who_am_i,
    myBasics: fullProfile.my_basics,
    aboutMyFamily: fullProfile.about_my_family,
    myEducation: fullProfile.my_education,
    residentialInformation: fullProfile.residential_information,
    businessDetails: fullProfile.business_details
  });
};


// Handle changes in the edit form:
const handleEditChange = (e) => {
  setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
};

// Similar to your Quill change for edit:
const handleEditQuillChange = (field, value) => {
  setEditFormData({ ...editFormData, [field]: value });
};

// Submit updated profile data:
const handleEditSubmit = async (e) => {
  e.preventDefault();
  try {
    const res = await fetch('/update_profile.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: editingProfile.id, ...editFormData })
    });
    const data = await res.json();
    if (!data.error) {
      setMessage('Profile updated successfully');
      fetchProfiles();
      setEditingProfile(null); // close modal
    } else {
      setMessage('Error: ' + data.error);
    }
  } catch (err) {
    setMessage('Error updating profile: ' + err.message);
  }
};

  const handleDelete = async (profileId) => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        const res = await fetch('/delete_profile.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: profileId })
        });
        const data = await res.json();
        if (!data.error) {
          setMessage('Profile deleted successfully');
          fetchProfiles();
        } else {
          setMessage('Error: ' + data.error);
        }
      } catch (err) {
        setMessage('Error deleting profile: ' + err.message);
      }
    }
  };

  // ReactQuill configurations
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean']
    ]
  };
  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet'
  ];

  // If not authenticated, show the login form
  if (!isAuthenticated) {
    return (
      <div className="admin-panel-container login-container">
        <h2>Admin Login</h2>
        <form onSubmit={handleLogin} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input 
              type="text" 
              id="username" 
              value={loginUsername} 
              onChange={(e) => setLoginUsername(e.target.value)} 
              required 
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input 
              type="password" 
              id="password" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)} 
              required 
            />
          </div>
          {loginError && <p className="error-message">{loginError}</p>}
          <button type="submit" className="submit-btn">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-panel-container">
      <h2>Admin Panel</h2>
      <div className="tabs">
        <button
          className={activeTab === 'create' ? 'active' : ''}
          onClick={() => { setActiveTab('create'); setMessage(''); }}
        >
          Create Profile
        </button>
        <button
          className={activeTab === 'manage' ? 'active' : ''}
          onClick={() => { setActiveTab('manage'); setMessage(''); }}
        >
          Manage Profiles
        </button>
      </div>

      {activeTab === 'create' && (
        <div className="create-profile">
          <form onSubmit={handleSubmit} className="admin-form">
            <fieldset>
              <legend>Basic Information</legend>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="subtitle">Subtitle</label>
                <input
                  type="text"
                  id="subtitle"
                  name="subtitle"
                  placeholder="Enter subtitle (e.g., Entrepreneur)"
                  value={formData.subtitle}
                  onChange={handleChange}
                  required
                />
              </div>
            </fieldset>
            
            <fieldset>
              <legend>Profile Details</legend>
              <div className="form-group">
                <label htmlFor="whoAmI">Who Am I</label>
                <ReactQuill
                  value={formData.whoAmI}
                  onChange={(value) => handleQuillChange('whoAmI', value)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
              <div className="form-group">
                <label htmlFor="myBasics">My Basics</label>
                <ReactQuill
                  value={formData.myBasics}
                  onChange={(value) => handleQuillChange('myBasics', value)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
              <div className="form-group">
                <label htmlFor="aboutMyFamily">About My Family</label>
                <ReactQuill
                  value={formData.aboutMyFamily}
                  onChange={(value) => handleQuillChange('aboutMyFamily', value)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
            </fieldset>

            <fieldset>
              <legend>Additional Information</legend>
              <div className="form-group">
                <label htmlFor="myEducation">My Education</label>
                <ReactQuill
                  value={formData.myEducation}
                  onChange={(value) => handleQuillChange('myEducation', value)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
              <div className="form-group">
                <label htmlFor="residentialInformation">Residential Information</label>
                <ReactQuill
                  value={formData.residentialInformation}
                  onChange={(value) => handleQuillChange('residentialInformation', value)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
              <div className="form-group">
                <label htmlFor="businessDetails">Business Details</label>
                <ReactQuill
                  value={formData.businessDetails}
                  onChange={(value) => handleQuillChange('businessDetails', value)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
            </fieldset>
            <fieldset>
            <legend>Photos</legend>
            <div className="form-group">
              <label htmlFor="photos">Upload Photos</label>
              <input
                type="file"
                id="photos"
                name="photos"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
              />
            </div>
            </fieldset>
            <button type="submit" className="submit-btn">Create Profile</button>
          </form>
          {message && <p className="message">{message}</p>}
          {shareableLink && (
            <div className="shareable-link">
              <p>Shareable Link:</p>
              <a href={shareableLink} target="_blank" rel="noopener noreferrer">
                {shareableLink}
              </a>
              <button onClick={() => handleCopyLink(shareableLink)} className="copy-btn">
                Copy Link
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'manage' && (
        <div className="manage-profiles">
          <h3>Manage Profiles</h3>
          {profiles.length === 0 ? (
            <p>No profiles found.</p>
          ) : (
            <table className="profiles-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subtitle</th>
                  <th>Created At</th>
                  <th>Shareable Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => {
                  const profileLink = `https://kanhamatrimonials.com/profile?token=${profile.token}`;
                  return (
                    <tr key={profile.id}>
                      <td>{profile.name}</td>
                      <td>{profile.subtitle}</td>
                      <td>{profile.created_at}</td>
                      <td>
                        <a href={profileLink} target="_blank" rel="noopener noreferrer">
                          View Profile
                        </a>
                        <button onClick={() => handleCopyLink(profileLink)} className="copy-btn">
                          Copy Link
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                        <button onClick={() => handleEdit(profile)}>Edit</button>
                          <button onClick={() => handleDelete(profile.id)}>Delete</button>
                          <button onClick={() => handleRegenerate(profile.id)}>Regenerate</button>
                          <button onClick={() => handleExpire(profile.id)}>Expire</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {editingProfile && (
  <div className="modal-overlay">
    <div className="modal-content">
      <h3>Edit Profile</h3>
      <form onSubmit={handleEditSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input 
            type="text" 
            name="name"
            value={editFormData.name}
            onChange={handleEditChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="subtitle">Subtitle</label>
          <input 
            type="text" 
            name="subtitle"
            value={editFormData.subtitle}
            onChange={handleEditChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Who Am I</label>
          <ReactQuill
            value={editFormData.whoAmI}
            onChange={(value) => handleEditQuillChange('whoAmI', value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <div className="form-group">
          <label>My Basics</label>
          <ReactQuill
            value={editFormData.myBasics}
            onChange={(value) => handleEditQuillChange('myBasics', value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <div className="form-group">
          <label>About My Family</label>
          <ReactQuill
            value={editFormData.aboutMyFamily}
            onChange={(value) => handleEditQuillChange('aboutMyFamily', value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <div className="form-group">
          <label>My Education</label>
          <ReactQuill
            value={editFormData.myEducation}
            onChange={(value) => handleEditQuillChange('myEducation', value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <div className="form-group">
          <label>Residential Information</label>
          <ReactQuill
            value={editFormData.residentialInformation}
            onChange={(value) => handleEditQuillChange('residentialInformation', value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <div className="form-group">
          <label>Business Details</label>
          <ReactQuill
            value={editFormData.businessDetails}
            onChange={(value) => handleEditQuillChange('businessDetails', value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </div>
        <div className="modal-buttons">
          <button type="submit" className="submit-btn">Update Profile</button>
          <button type="button" className="cancel-btn" onClick={() => setEditingProfile(null)}>Cancel</button>
        </div>
      </form>
    </div>
  </div>
)}


    </div>
  );
};

export default AdminPanel;
